// packages/character-render/src/proceduralHumanoid.ts
//
// Role templates and the deterministic role -> spec derivation (seeded by an
// NPC id). Split out of proceduralHumanoid.ts (which re-exports the public
// names and keeps the part builder).
import * as THREE from 'three';
import type {
  BuildStyle,
  CoatStyle,
  HatStyle,
  HumanoidPalette,
  ProceduralHumanoidSpec,
} from './humanoidSpec';

export type HumanoidRole =
  | 'merchant'
  | 'sailor'
  | 'officer'
  | 'harbormaster'
  | 'guard'
  | 'guide'
  | 'noble'
  | 'villager';

export const HUMANOID_ROLES: readonly HumanoidRole[] = [
  'merchant',
  'sailor',
  'officer',
  'harbormaster',
  'guard',
  'guide',
  'noble',
  'villager',
];

const SKIN = ['#f1c9a5', '#e2b088', '#c98e62', '#a86d45', '#7e4d2f', '#5c3822'];
const HAIR = ['#1f1a17', '#3b2717', '#5e3b20', '#94652f', '#7d7a76'];

interface RoleTemplate {
  build: BuildStyle[];
  height: number;
  hat: HatStyle[];
  coat: CoatStyle;
  beardChance: number;
  stripes?: boolean;
  epaulettes?: boolean;
  apron?: boolean;
  sash?: boolean;
  cutlass?: boolean;
  clothes: Array<Omit<HumanoidPalette, 'skin' | 'hair'>>;
}

const ROLE_TEMPLATES: Record<HumanoidRole, RoleTemplate> = {
  merchant: {
    build: ['stout', 'regular'],
    height: 1.82,
    hat: ['turban', 'cap'],
    coat: 'none',
    beardChance: 0.6,
    apron: true,
    clothes: [
      {
        shirt: '#a8452f',
        pants: '#4a3526',
        boots: '#2e211a',
        belt: '#1f1611',
        hat: '#e9dfc8',
        accent: '#e6d8b8',
      },
      {
        shirt: '#6f4a8a',
        pants: '#3d3228',
        boots: '#2b201a',
        belt: '#1c1510',
        hat: '#d8b04a',
        accent: '#efe3c6',
      },
      {
        shirt: '#b5822f',
        pants: '#4b3b2b',
        boots: '#2e221b',
        belt: '#23190f',
        hat: '#f1ead8',
        accent: '#dccfae',
      },
    ],
  },
  sailor: {
    build: ['slim', 'regular'],
    height: 1.8,
    hat: ['bandana', 'bandana', 'cap'],
    coat: 'none',
    beardChance: 0.4,
    stripes: true,
    clothes: [
      {
        shirt: '#eeeae0',
        shirtAlt: '#23365e',
        pants: '#c7b58a',
        boots: '#3a2a1e',
        belt: '#5a3b20',
        hat: '#b3261e',
        accent: '#b3261e',
      },
      {
        shirt: '#eeeae0',
        shirtAlt: '#8e1f1f',
        pants: '#3e4f6b',
        boots: '#2d2119',
        belt: '#4a3019',
        hat: '#2c4a7a',
        accent: '#2c4a7a',
      },
    ],
  },
  officer: {
    build: ['regular', 'slim'],
    height: 1.96,
    hat: ['tricorn'],
    coat: 'long',
    beardChance: 0.15,
    epaulettes: true,
    clothes: [
      {
        shirt: '#f2efe6',
        coat: '#1f2d57',
        pants: '#ece6d6',
        boots: '#141414',
        belt: '#141414',
        hat: '#15151a',
        accent: '#d4a93c',
      },
      {
        shirt: '#f2efe6',
        coat: '#7d1c1c',
        pants: '#ece6d6',
        boots: '#141414',
        belt: '#141414',
        hat: '#15151a',
        accent: '#d4a93c',
      },
    ],
  },
  harbormaster: {
    build: ['stout', 'regular'],
    height: 1.9,
    hat: ['tricorn'],
    coat: 'long',
    beardChance: 0.85,
    clothes: [
      {
        shirt: '#ece0c4',
        coat: '#5b3a22',
        pants: '#2f2a26',
        boots: '#1d1712',
        belt: '#1a120c',
        hat: '#2a1d14',
        accent: '#c9a24a',
      },
      {
        shirt: '#e8dcc0',
        coat: '#2f4a4a',
        pants: '#2c2824',
        boots: '#1d1712',
        belt: '#1a120c',
        hat: '#1f1f1f',
        accent: '#c9a24a',
      },
    ],
  },
  guard: {
    build: ['regular', 'stout'],
    height: 1.94,
    hat: ['helmet'],
    coat: 'short',
    beardChance: 0.5,
    cutlass: true,
    clothes: [
      {
        shirt: '#d9d2c2',
        coat: '#7a1f1f',
        pants: '#5a5550',
        boots: '#1f1a16',
        belt: '#2a1d12',
        accent: '#c9a24a',
      },
      {
        shirt: '#d9d2c2',
        coat: '#2c3f6b',
        pants: '#55524e',
        boots: '#1f1a16',
        belt: '#2a1d12',
        accent: '#e0e0e0',
      },
    ],
  },
  guide: {
    build: ['slim'],
    height: 1.78,
    hat: ['straw', 'none'],
    coat: 'none',
    beardChance: 0.25,
    sash: true,
    clothes: [
      {
        shirt: '#e6dbbd',
        pants: '#8a6a45',
        boots: '#4a3322',
        belt: '#3a2716',
        hat: '#d9bf73',
        accent: '#2f7d6d',
      },
      {
        shirt: '#d7e0d4',
        pants: '#6d5a44',
        boots: '#3f2c1f',
        belt: '#3a2716',
        hat: '#cfb56a',
        accent: '#b8492a',
      },
    ],
  },
  noble: {
    build: ['regular', 'stout'],
    height: 1.88,
    hat: ['turban', 'tricorn'],
    coat: 'robe',
    beardChance: 0.7,
    sash: true,
    clothes: [
      {
        shirt: '#f3ead2',
        coat: '#4a2767',
        pants: '#2a2230',
        boots: '#1a1418',
        belt: '#c9a24a',
        hat: '#f1e6c8',
        accent: '#d4a93c',
      },
      {
        shirt: '#f3ead2',
        coat: '#16594f',
        pants: '#1f2a28',
        boots: '#15120f',
        belt: '#c9a24a',
        hat: '#e9d9b0',
        accent: '#d4a93c',
      },
    ],
  },
  villager: {
    build: ['regular', 'slim', 'stout'],
    height: 1.8,
    hat: ['none', 'cap', 'straw'],
    coat: 'none',
    beardChance: 0.35,
    clothes: [
      {
        shirt: '#b99a6b',
        pants: '#5a4633',
        boots: '#33251a',
        belt: '#2a1d12',
        hat: '#8a6a45',
        accent: '#e8dcc0',
      },
      {
        shirt: '#7c8f5a',
        pants: '#4d4032',
        boots: '#2e2218',
        belt: '#2a1d12',
        hat: '#d9bf73',
        accent: '#e8dcc0',
      },
    ],
  },
};

/** FNV-1a 32-bit hash of a string — stable seed from an NPC id. */
export function hashSeed(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function jitter(hex: string, rnd: () => number, amount = 0.035): string {
  const _c = new THREE.Color();
  _c.set(hex);
  _c.offsetHSL((rnd() - 0.5) * 0.02, (rnd() - 0.5) * amount, (rnd() - 0.5) * amount);
  return `#${_c.getHexString()}`;
}

function pick<T>(list: readonly T[], rnd: () => number): T {
  return list[Math.floor(rnd() * list.length) % list.length]!;
}

/** Deterministic spec for a role; `seedKey` (e.g. the NPC id) picks the variant. */
export function humanoidSpecForRole(role: HumanoidRole, seedKey: string): ProceduralHumanoidSpec {
  const tpl = ROLE_TEMPLATES[role];
  const rnd = mulberry32(hashSeed(`${role}:${seedKey}`));
  const clothes = pick(tpl.clothes, rnd);
  const palette: HumanoidPalette = {
    skin: pick(SKIN, rnd),
    hair: pick(HAIR, rnd),
    shirt: jitter(clothes.shirt, rnd),
    ...(clothes.shirtAlt ? { shirtAlt: jitter(clothes.shirtAlt, rnd) } : {}),
    ...(clothes.coat ? { coat: jitter(clothes.coat, rnd) } : {}),
    pants: jitter(clothes.pants, rnd),
    boots: clothes.boots,
    belt: clothes.belt,
    ...(clothes.hat ? { hat: jitter(clothes.hat, rnd) } : {}),
    accent: clothes.accent,
  };
  return {
    height: tpl.height * (0.97 + rnd() * 0.06),
    build: pick(tpl.build, rnd),
    palette,
    hat: pick(tpl.hat, rnd),
    coat: tpl.coat,
    stripes: tpl.stripes ?? false,
    beard: rnd() < tpl.beardChance,
    epaulettes: tpl.epaulettes ?? false,
    apron: tpl.apron ?? false,
    sash: tpl.sash ?? false,
    cutlass: tpl.cutlass ?? false,
  };
}
